import NoData from "@rio-cloud/rio-uikit/NoData";
import DummyRowDropdown from "./DummyRowDropdown";
import SharedTable, { resolveDisplaySortKey } from "../SharedTable";
import type { SharedTableBaseProps, SharedVehicleRow } from "../SharedTable";
import { buildTripDetailsUrl, openTripDetails } from "@utils/routingDeepLink";
import { renderDriverContent } from "../SharedDriverCell";
import { useT } from "@hooks";

type SortKey<T> = keyof T | [keyof T, keyof T] | null;

export type ReceiptStatus = "previsto" | "realizado" | "emAtraso";

export type Vehicle = SharedVehicleRow<ReceiptStatus>;

const STATUS_BADGE_CLASS: Record<ReceiptStatus, string> = {
  previsto: "badge bg-lightest border border-color-lighter",
  realizado: "badge badge-soft-success",
  emAtraso: "badge badge-soft-danger",
};

interface Props extends SharedTableBaseProps<Vehicle, SortKey<Vehicle>> {
  handleDelete: (id: string) => void;
  onDeleteFile?: (id: string) => void;
  onGetFile?: (id: string) => void;
  handleEdit: (id: string) => void;
  handleObservation: (id: string) => void;
  handleAudit: (id: string) => void;
}

const Table = ({
  rows,
  isViewTypeTable,
  tableRef,
  tableClassNames,
  columns,
  columnLabels,
  columnsDetails,
  sortKey,
  sortDirection,
  handleSortChange,
  onActiveRowChange,
  handleDelete,
  handleEdit,
  handleObservation,
  handleAudit,
  onDeleteFile,
  onGetFile,
  isLoading = false,
}: Props) => {
  const t = useT();

  const renderCellContent = (row: Vehicle, col: string, item: React.ReactNode) => {
    if (col === "driver") {
      return <div className="text-capitalize">{renderDriverContent(row.driver)}</div>;
    }

    if (col === "status" && row.status) {
      return <span className={STATUS_BADGE_CLASS[row.status]}>{t(`receipt.status.${row.status}`)}</span>;
    }

    return <span>{item}</span>;
  };

  return (
    <SharedTable<Vehicle>
      rows={rows}
      isViewTypeTable={isViewTypeTable}
      tableRef={tableRef}
      tableClassNames={tableClassNames}
      columns={columns}
      columnsDetails={columnsDetails}
      columnLabels={columnLabels}
      sortBy={resolveDisplaySortKey(sortKey)}
      sortDir={sortKey ? sortDirection : undefined}
      handleSortChange={handleSortChange}
      onActiveRowChange={onActiveRowChange}
      notFoundMessage={t("recipe.table.notFound")}
      containerClassName="table-responsive min-height-50pct"
      containerStyle={{ position: "relative" }}
      isLoading={isLoading}
      renderCell={(row, col) => renderCellContent(row, col, row[col as keyof Vehicle] ?? <NoData text="-" />)}
      renderRowActions={(row) => (
        <td className="table-action">
          <DummyRowDropdown
            items={[
              {
                icon: "rioglyph rioglyph-download",
                onClick: () => onGetFile?.(row.id),
                title: t("expense.form.action.downloadReceipt"),
              },
              {
                icon: "rioglyph rioglyph-trash ",
                onClick: () => onDeleteFile?.(row.id),
                title: t("expense.form.action.deleteReceipt"),
              },
              {
                icon: "rioglyph rioglyph-time",
                onClick: () => handleAudit(row.id),
                title: t("common.action.audit"),
              },
              {
                icon: "rioglyph rioglyph-route",
                onClick: () => openTripDetails({ tripId: row.tripId, driverId: row.tripDriverId }),
                title: t("common.action.viewTripDetails"),
                disabled: !buildTripDetailsUrl({ tripId: row.tripId, driverId: row.tripDriverId }),
                disabledTooltip: t("common.action.viewTripDetails.disabled"),
              },
              {
                icon: "rioglyph rioglyph-pencil",
                onClick: () => handleEdit(row.id),
                title: t("common.action.edit"),
              },
              {
                icon: "rioglyph rioglyph-trash",
                onClick: () => handleDelete(row.id),
                title: t("common.action.delete"),
              },
            ]}
          />
        </td>
      )}
    />
  );
};

export default Table;
